import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const CourtDeadlinesWidget = ({ viewType, userId }) => {
  const [courtDeadlines, setCourtDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleDeadlineClick = (deadline) => {
    // Navigate to calendar with the specific date
    const date = new Date(deadline.due_date).toISOString().split('T')[0];
    navigate(`/calendar?date=${date}`, { 
      state: { 
        highlightEvent: deadline.calendar_event_id 
      } 
    });
  };

  const handleViewAllClick = () => {
    // Navigate to calendar with upcoming deadlines view
    navigate('/calendar?view=deadlines');
  };

  const fetchCourtDeadlines = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dashboardService.getCourtDeadlines(viewType);
      setCourtDeadlines(response.data.deadlines || []);
    } catch (error) {
      console.error('Error fetching court deadlines:', error);
      setCourtDeadlines([]);
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    fetchCourtDeadlines();
  }, [fetchCourtDeadlines]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'court_appearance':
        return <Calendar className="h-4 w-4" />;
      case 'filing':
        return <Clock className="h-4 w-4" />;
      case 'deposition':
        return <MapPin className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'court_appearance':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'filing':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'deposition':
        return 'text-yellow-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDaysUntil = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `In ${days} days`;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-56"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-100 rounded"></div>
            <div className="h-20 bg-gray-100 rounded"></div>
            <div className="h-20 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
      {/* Widget Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar className="h-5 w-5 text-gray-600 mr-2" />
            Court Deadlines This Week
            <span className="ml-2 bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              {courtDeadlines.length}
            </span>
          </h3>
          {courtDeadlines.length > 0 && (
            <button className="text-yellow-600 hover:text-gray-800 text-sm font-medium">
              View Calendar
            </button>
          )}
        </div>
      </div>

      {/* Widget Content */}
      <div className="p-6">
        {courtDeadlines.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600 font-medium">No court deadlines</p>
            <p className="text-gray-500 text-sm">You have a clear schedule this week.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {courtDeadlines.slice(0, 4).map((deadline) => {
              const { date, time } = formatDateTime(deadline.due_date);
              return (
                <div
                  key={deadline.id}
                  onClick={() => handleDeadlineClick(deadline)}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-blue-300 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className={`p-1 rounded border ${getTypeColor(deadline.type)}`}>
                          {getTypeIcon(deadline.type)}
                        </div>
                        <h4 className="font-medium text-gray-900">{deadline.title}</h4>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">
                        {deadline.case_number} - {deadline.case_title}
                      </p>

                      {viewType === 'firm' && deadline.assigned_to && (
                        <div className="text-sm text-gray-500 mb-2">
                          <div>Assigned to: {deadline.assigned_to}</div>
                          {deadline.assigned_to_email && (
                            <div className="text-xs text-gray-400">{deadline.assigned_to_email}</div>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {date} at {time}
                          </span>
                          <span className="text-yellow-600 font-medium">
                            {formatDaysUntil(deadline.days_until)}
                          </span>
                        </div>
                      </div>

                      {deadline.location && (
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <MapPin className="h-4 w-4 mr-1" />
                          {deadline.location}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {courtDeadlines.length > 4 && (
              <div className="text-center pt-4 border-t border-gray-200">
                <button 
                  onClick={handleViewAllClick}
                  className="text-yellow-600 hover:text-gray-800 text-sm font-medium"
                >
                  View {courtDeadlines.length - 4} more upcoming deadlines
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourtDeadlinesWidget;